import React from 'react';

import MuiAutocomplete, {
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import { AutocompleteGetTagProps } from '@mui/material/useAutocomplete';

import Chip, { ChipProps } from '@libs/ui/components/chip/Chip';
import wrapControlWithGraph from '@libs/ui/components/form/hocs/wrapControlWithGraph';
import { OptionType } from '@libs/ui/components/form/select';
import TextField, {
  TextFieldProps,
} from '@libs/ui/components/form/text-field/TextField';

import withStyles from './styles';

export interface MultipleSelectProps extends Omit<
  MuiAutocompleteProps<OptionType, true, false, false>,
  'renderInput'
> {
  chipProps?: ChipProps | ((option: OptionType) => ChipProps);
  inputProps?: TextFieldProps;
  helperText?: string;
  autoCompleteProps?: any;
  error?: boolean;
  name?: string;
  noOptionsText?: string | React.ReactNode;
  variant?: 'standard' | 'outlined' | 'filled';
  fullWidth?: boolean;
  formControlProps?: any;
  hideNoneValue?: boolean;
  label?: string;
}

const MultipleSelect = (props: MultipleSelectProps) => {
  const classes = withStyles(props);
  const {
    options,
    chipProps,
    inputProps,
    autoCompleteProps = {},
    helperText,
    error,
    noOptionsText,
    renderTags,
  } = props;

  const commonRenderTags = (
    value: OptionType[],
    getTagProps: AutocompleteGetTagProps,
  ) =>
    value.map((option: OptionType, index: number) => {
      const { key, ...tagProps } = getTagProps({ index });
      return (
        <Chip
          key={key}
          {...(typeof chipProps === 'function' ? chipProps(option) : chipProps)}
          size="small"
          label={typeof option === 'string' ? option : option.text}
          {...tagProps}
        />
      );
    });

  return (
    <MuiAutocomplete
      multiple
      {...autoCompleteProps}
      fullWidth
      ListboxProps={{
        ...autoCompleteProps?.ListboxProps,
        'data-test-id': 'select-listbox',
      }}
      options={options}
      noOptionsText={noOptionsText}
      isOptionEqualToValue={(option: OptionType, value: OptionType) => {
        return option?.value === value?.value;
      }}
      getOptionLabel={(option: OptionType) => option?.text ?? ''}
      renderTags={renderTags || commonRenderTags || ''}
      renderInput={(params) => {
        return (
          <TextField
            error={error}
            {...params}
            {...inputProps}
            inputProps={{
              ...params.inputProps,
              ...inputProps?.inputProps,
            }}
            InputLabelProps={{
              ...params?.InputLabelProps,
              ...inputProps?.InputLabelProps,
              classes: {
                shrink: classes.labelShrink,
              },
            }}
            helperText={helperText}
          />
        );
      }}
    />
  );
};

export default MultipleSelect;
export const GraphMultipleSelect =
  wrapControlWithGraph<Omit<MultipleSelectProps, 'options'>>(MultipleSelect);
